/**
 * Immersive browser chrome for the connected kiosk player.
 * Pairing stays windowed (so the code field stays usable); once connected we
 * hide browser UI via the Fullscreen API and re-arm on the next gesture if
 * the browser blocked the first attempt (common on cold-start restore).
 */

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenEnabled?: boolean;
};

export function isKioskFullscreen(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(document.fullscreenElement ?? doc.webkitFullscreenElement);
}

/** Enter immersive mode and hide browser navigation UI when the engine supports it. */
export async function requestKioskFullscreen(el: HTMLElement): Promise<boolean> {
  if (isKioskFullscreen()) return true;

  const target = el as FullscreenElement;
  try {
    if (typeof target.requestFullscreen === 'function') {
      await target.requestFullscreen({ navigationUI: 'hide' });
      return isKioskFullscreen();
    }
    if (typeof target.webkitRequestFullscreen === 'function') {
      await Promise.resolve(target.webkitRequestFullscreen());
      return isKioskFullscreen();
    }
    if (typeof target.msRequestFullscreen === 'function') {
      await Promise.resolve(target.msRequestFullscreen());
      return isKioskFullscreen();
    }
  } catch {
    // Autoplay / missing user-gesture policies deny fullscreen; caller may arm a gesture.
  }
  return false;
}

export async function exitKioskFullscreen(): Promise<void> {
  if (!isKioskFullscreen()) return;
  const doc = document as FullscreenDocument;
  try {
    if (typeof document.exitFullscreen === 'function') {
      await document.exitFullscreen();
      return;
    }
    if (typeof doc.webkitExitFullscreen === 'function') {
      await Promise.resolve(doc.webkitExitFullscreen());
      return;
    }
    doc.msExitFullscreen?.();
  } catch {
    // Ignore — returning to pairing still works windowed.
  }
}

/**
 * Try fullscreen immediately, then keep a one-shot gesture listener until it
 * succeeds. Re-arms if the operator exits fullscreen while still playing.
 * Returns a disposer for phase teardown.
 */
export function lockKioskFullscreen(el: HTMLElement): () => void {
  let disposed = false;
  let gestureArmed = false;

  const disarmGesture = () => {
    if (!gestureArmed) return;
    gestureArmed = false;
    window.removeEventListener('pointerdown', onGesture, true);
    window.removeEventListener('keydown', onGesture, true);
  };

  const armGesture = () => {
    if (disposed || gestureArmed || isKioskFullscreen()) return;
    gestureArmed = true;
    window.addEventListener('pointerdown', onGesture, true);
    window.addEventListener('keydown', onGesture, true);
  };

  const onGesture = () => {
    if (disposed) return;
    disarmGesture();
    void requestKioskFullscreen(el).then((ok) => {
      if (disposed) return;
      if (!ok) armGesture();
    });
  };

  const onFullscreenChange = () => {
    if (disposed) return;
    if (isKioskFullscreen()) disarmGesture();
    else armGesture();
  };

  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);

  void requestKioskFullscreen(el).then((ok) => {
    if (disposed) return;
    if (!ok) armGesture();
  });

  return () => {
    disposed = true;
    disarmGesture();
    document.removeEventListener('fullscreenchange', onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
  };
}

export async function requestWakeLock(): Promise<WakeLockSentinel | null> {
  try {
    if (!('wakeLock' in navigator)) return null;
    return await navigator.wakeLock.request('screen');
  } catch {
    return null;
  }
}
